// hooks/useAISession.ts

import { useState, useCallback, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  useCreateAISessionMutation,
  useGetAISessionsQuery,
  useGetSessionMessagesQuery,
  useSendAIMessageMutation,
  useDeleteAISessionMutation,
} from '@/store/features/ai-events/aiEventsAPI';
import {
  setCurrentSession,
  clearCurrentSession,
  addMessage,
} from '@/store/features/ai-events/aiEventsSlice';
import { AISession, AIMessage } from '@/store/features/ai-events/aiEventsTypes';

export const useAISession = (sessionId?: string) => {
  const dispatch = useAppDispatch();
  const { currentSession, messages, isLoadingMessages } = useAppSelector(
    (state) => state.aiEvents
  );

  const [createSession] = useCreateAISessionMutation();
  const [sendMessage] = useSendAIMessageMutation();
  const [deleteSession] = useDeleteAISessionMutation();

  const { data: sessionsData } = useGetAISessionsQuery();
  const { data: messagesData, refetch: refetchMessages } = useGetSessionMessagesQuery(
    { sessionId: sessionId || '' },
    { skip: !sessionId }
  );

  // Create new session
  const create = useCallback(
    async (title?: string, context?: string) => {
      try {
        const session = await createSession({
          title: title || 'New Event Planning Session',
          context,
        }).unwrap();

        dispatch(setCurrentSession(session));
        return session;
      } catch (error) {
        console.error('Failed to create session:', error);
        throw error;
      }
    },
    [createSession, dispatch]
  );

  // Send message to current session
  const send = useCallback(
    async (message: string, context?: Record<string, any>) => {
      if (!currentSession) {
        throw new Error('No active session');
      }

      try {
        const aiMessage = await sendMessage({
          session_id: currentSession.documentId,
          message,
          context,
        }).unwrap();

        dispatch(addMessage(aiMessage));
        return aiMessage;
      } catch (error) {
        console.error('Failed to send message:', error);
        throw error;
      }
    },
    [currentSession, sendMessage, dispatch]
  );

  // Delete session
  const remove = useCallback(
    async (id: string) => {
      try {
        await deleteSession(id).unwrap();
      } catch (error) {
        console.error('Failed to delete session:', error);
        throw error;
      }
    },
    [deleteSession]
  );

  // Clear current session
  const clear = useCallback(() => {
    dispatch(clearCurrentSession());
  }, [dispatch]);

  return {
    // State
    currentSession,
    sessions: sessionsData?.data || [],
    messages,
    isLoadingMessages,

    // Methods
    create,
    send,
    remove,
    clear,
    refetchMessages,
  };
};
